import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { DateSelection } from './wizard/DateSelection';
import { DeliveryMethod } from './wizard/DeliveryMethod';
import { UserDetails } from './wizard/UserDetails';
import { Coordination } from './wizard/Coordination';
import { Agreement } from './wizard/Agreement';
import { Confirmation } from './wizard/Confirmation';
import { submitBooking, validateBooking } from '../utils/appsScript';
import { Booking } from '../types/booking';
interface BookingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  existingBookings: Booking[];
  onBookingSuccess?: () => void;
}
type Step = 1 | 2 | 3 | 4 | 5 | 6;
export function BookingWizard({
  isOpen,
  onClose,
  existingBookings,
  onBookingSuccess
}: BookingWizardProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Form State
  const [bookingData, setBookingData] = useState({
    startDate: '',
    endDate: '',
    method: null as 'pickup' | 'dropoff' | null,
    firstName: '',
    surname: '',
    farm: '',
    location: '',
    email: '',
    phone: '',
    agreementAccepted: false
  });
  if (!isOpen) return null;
  // Always show user details (for both pickup and dropoff)
  const shouldShowUserDetails = true;
  // Only show coordination for pickup
  const shouldShowCoordination = bookingData.method === 'pickup';
  const handleSubmitBooking = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Convert date format from YYYY-MM-DD to DD/MM/YYYY
      const formatDateToDDMMYYYY = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };
      // Determine pickup method and location
      const pickupMethod = bookingData.method === 'pickup' ? 'farm-pickup' : 'wmf-pickup';
      // Find who has the machine before this booking
      const getPreviousFarmer = () => {
        const selectedStartTime = new Date(bookingData.startDate).getTime();
        const previousBooking = existingBookings.filter(booking => {
          if (booking.status === 'completed') return false;
          const [day, month, year] = booking.endDate.split('/');
          const bookingEndDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          const bookingEndTime = new Date(bookingEndDate).getTime();
          return bookingEndTime < selectedStartTime;
        }).sort((a, b) => {
          const [dayA, monthA, yearA] = a.endDate.split('/');
          const [dayB, monthB, yearB] = b.endDate.split('/');
          const dateA = new Date(`${yearA}-${monthA.padStart(2, '0')}-${dayA.padStart(2, '0')}`).getTime();
          const dateB = new Date(`${yearB}-${monthB.padStart(2, '0')}-${dayB.padStart(2, '0')}`).getTime();
          return dateB - dateA;
        })[0];
        return previousBooking;
      };
      const previousFarmer = getPreviousFarmer();
      const pickupFrom = bookingData.method === 'pickup' && previousFarmer ? `${previousFarmer.userName} at ${previousFarmer.location || previousFarmer.farm}` : 'Stanthorpe WMF';
      // Prepare booking data
      const submissionData = {
        userId: `user-${Date.now()}`,
        userName: bookingData.firstName && bookingData.surname ? `${bookingData.firstName} ${bookingData.surname}` : 'Anonymous User',
        startDate: formatDateToDDMMYYYY(bookingData.startDate),
        endDate: formatDateToDDMMYYYY(bookingData.endDate),
        location: bookingData.location || bookingData.farm || 'Location TBD',
        status: 'upcoming' as const,
        notes: `Pickup method: ${pickupMethod}`,
        firstName: bookingData.firstName || '',
        surname: bookingData.surname || '',
        farm: bookingData.farm || '',
        email: bookingData.email || '',
        phone: bookingData.phone || '',
        pickupMethod: pickupMethod,
        pickupFrom: pickupFrom,
        dropoffMethod: 'pending' as const,
        dropoffTo: 'TBD' // Will be calculated by Apps Script
      };
      // Validate
      const validation = validateBooking(submissionData);
      if (!validation.valid) {
        setError(validation.errors.join(', '));
        setSubmitting(false);
        return;
      }
      // Submit
      const result = await submitBooking(submissionData);
      if (result.success) {
        // NEW: Trigger immediate refresh
        if (onBookingSuccess) {
          onBookingSuccess();
        }
        // Success - close modal and reset
        setTimeout(() => {
          onClose();
          setStep(1);
          setBookingData({
            startDate: '',
            endDate: '',
            method: null,
            firstName: '',
            surname: '',
            farm: '',
            location: '',
            email: '',
            phone: '',
            agreementAccepted: false
          });
          setSubmitting(false);
          setError(null);
        }, 1500);
      } else {
        setError(result.message);
        setSubmitting(false);
      }
    } catch (err) {
      setError('Failed to submit booking. Please try again.');
      setSubmitting(false);
    }
  };
  const nextStep = () => {
    if (step === 1) {
      setDirection(1);
      setStep(2);
    } else if (step === 2) {
      setDirection(1);
      setStep(3);
    } else if (step === 3) {
      if (bookingData.method === 'pickup') {
        setDirection(1);
        setStep(4);
      } else {
        // Skip coordination for dropoff, go to agreement
        setDirection(1);
        setStep(5);
      }
    } else if (step === 4) {
      // Coordination → Agreement
      setDirection(1);
      setStep(5);
    } else if (step === 5) {
      // Agreement → Confirmation
      setDirection(1);
      setStep(6);
    } else if (step === 6) {
      // Final step - submit booking
      handleSubmitBooking();
    }
  };
  const prevStep = () => {
    if (step === 5 && bookingData.method === 'dropoff') {
      // Skip back over coordination for dropoff
      setDirection(-1);
      setStep(3);
    } else if (step === 6 && bookingData.method === 'dropoff') {
      // Go back to agreement
      setDirection(-1);
      setStep(5);
    } else if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const isStepValid = () => {
    switch (step) {
      case 1:
        return !!bookingData.startDate && !!bookingData.endDate;
      case 2:
        return !!bookingData.method;
      case 3:
        return !!(bookingData.firstName.trim() && bookingData.surname.trim() && bookingData.farm.trim() && bookingData.location.trim() && bookingData.email.trim() && bookingData.phone.trim());
      case 4:
        return true;
      case 5:
        return bookingData.agreementAccepted;
      case 6:
        return true;
      default:
        return false;
    }
  };
  const getTotalSteps = () => {
    if (bookingData.method === 'pickup') {
      return 6; // Dates, Method, User Details, Coordination, Agreement, Confirmation
    } else {
      return 5; // Dates, Method, User Details, Agreement, Confirmation
    }
  };
  const getCurrentStepNumber = () => {
    if (step <= 3) return step;
    if (bookingData.method === 'pickup') {
      return step;
    } else {
      // Dropoff flow: skip step 4 (coordination)
      if (step === 5) return 4;
      if (step === 6) return 5;
      return step;
    }
  };
  const steps = [{
    title: 'Select Dates',
    component: DateSelection
  }, {
    title: 'Delivery Method',
    component: DeliveryMethod
  }, {
    title: 'Your Details',
    component: UserDetails
  }, {
    title: 'Coordination',
    component: Coordination
  }, {
    title: 'Agreement',
    component: Agreement
  }, {
    title: 'Confirm',
    component: Confirmation
  }];
  const CurrentComponent = steps[step - 1].component;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-181">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-182" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.2
    }} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-183">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white z-10" data-id="element-184">
          <div data-id="element-185">
            <h2 className="text-lg font-bold text-slate-900" data-id="element-186">New Booking</h2>
            <p className="text-xs text-slate-500" data-id="element-187">
              Step {getCurrentStepNumber()} of {getTotalSteps()}
            </p>
          </div>
          <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50" data-id="element-188">
            <X size={20} data-id="element-189" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-slate-100 w-full" data-id="element-190">
          <motion.div className="h-full bg-slate-900" animate={{
          width: `${getCurrentStepNumber() / getTotalSteps() * 100}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-191" />
        </div>

        {/* Error Message */}
        {error && <div className="mx-6 mt-4 bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-700" data-id="element-192">
            <p className="font-medium" data-id="element-193">Error</p>
            <p className="opacity-90 mt-0.5" data-id="element-194">{error}</p>
          </div>}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 relative min-h-[400px]" data-id="element-195">
          <AnimatePresence mode="wait" custom={direction} data-id="element-196">
            <motion.div key={step} custom={direction} initial={{
            x: direction * 20,
            opacity: 0
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: direction * -20,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="h-full" data-id="element-197">
              <CurrentComponent startDate={bookingData.startDate} endDate={bookingData.endDate} method={bookingData.method} firstName={bookingData.firstName} surname={bookingData.surname} farm={bookingData.farm} location={bookingData.location} email={bookingData.email} phone={bookingData.phone} agreed={bookingData.agreementAccepted} existingBookings={existingBookings} onSelect={(arg1: any, arg2: any) => {
              if (step === 1) {
                setBookingData(prev => ({
                  ...prev,
                  startDate: arg1,
                  endDate: arg2
                }));
              } else if (step === 2) {
                setBookingData(prev => ({
                  ...prev,
                  method: arg1
                }));
              }
            }} onUpdate={(field: string, value: string) => {
              if (step === 3) {
                setBookingData(prev => ({
                  ...prev,
                  [field]: value
                }));
              } else if (step === 5) {
                setBookingData(prev => ({
                  ...prev,
                  agreementAccepted: value === 'true'
                }));
              }
            }} data={bookingData} data-id="element-198" />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center" data-id="element-199">
          <button onClick={prevStep} disabled={step === 1 || submitting} className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${step === 1 || submitting ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'}
            `} data-id="element-200">
            <ArrowLeft size={16} data-id="element-201" />
            Back
          </button>

          <button onClick={nextStep} disabled={!isStepValid() || submitting} className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold shadow-sm transition-all duration-200
              ${!isStepValid() || submitting ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-md hover:-translate-y-0.5'}
            `} data-id="element-202">
            {submitting ? <>
                <Loader2 size={16} className="animate-spin" data-id="element-203" />
                Submitting...
              </> : <>
                {step === 6 ? 'Confirm Booking' : step === 5 ? 'Accept & Continue' : 'Next Step'}
                {step === 6 ? <Check size={16} data-id="element-204" /> : <ArrowRight size={16} data-id="element-205" />}
              </>}
          </button>
        </div>
      </motion.div>
    </div>;
}